import { DropEvent, FileWithPath } from 'react-dropzone';

// react-dropzone's default file-selector rejects the entire folder drop when a single
// browser filesystem entry fails to materialize. Chromium can do that for very deep
// Windows paths with NotFoundError, so this reader keeps the readable files and reports
// skipped entries instead of aborting the import before our upload validation runs.
export type TSkippedDropzoneEntry = {
  name?: string;
  path?: string;
  error: unknown;
};

type TDropzoneFilesOptions = {
  onSkippedEntries?: (entries: TSkippedDropzoneEntry[]) => void;
};

type TFileReadResult = {
  files: FileWithPath[];
  skippedEntries: TSkippedDropzoneEntry[];
};

type TBrowserEntryBase = {
  isDirectory: boolean;
  isFile: boolean;
  name: string;
  fullPath: string;
};

type TBrowserDirectoryReader = {
  readEntries: (successCallback: (entries: TBrowserEntry[]) => void, errorCallback?: (error: unknown) => void) => void;
};

type TBrowserDirectoryEntry = TBrowserEntryBase & {
  createReader: () => TBrowserDirectoryReader;
};

type TBrowserFileEntry = TBrowserEntryBase & {
  file: (successCallback: (file: File) => void, errorCallback?: (error: unknown) => void) => void;
};

type TBrowserEntry = TBrowserDirectoryEntry | TBrowserFileEntry;

type TBrowserFileHandle = {
  name: string;
  getFile: () => Promise<File>;
};

const DEFAULT_FILE_PATH_PREFIX = './';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const fromList = <T>(items: { length: number; [index: number]: T } | null | undefined): T[] => {
  if (!items) return [];

  const result: T[] = [];
  for (let i = 0; i < items.length; i++) result.push(items[i]);
  return result;
};

const defineReadonlyStringProp = (file: FileWithPath, key: 'path' | 'relativePath', value: string): void => {
  if (typeof file[key] === 'string') return;
  Object.defineProperty(file, key, {
    value,
    writable: false,
    configurable: false,
    enumerable: true,
  });
};

const toFileWithRelativePath = (file: File, path?: string): FileWithPath => {
  const fileWithPath = file as FileWithPath;
  const relativePath =
    path || (file.webkitRelativePath.length > 0 ? file.webkitRelativePath : `${DEFAULT_FILE_PATH_PREFIX}${file.name}`);

  defineReadonlyStringProp(fileWithPath, 'path', relativePath);
  defineReadonlyStringProp(fileWithPath, 'relativePath', relativePath);

  return fileWithPath;
};

const toSkippedEntry = (
  error: unknown,
  entry?: Pick<TBrowserEntryBase, 'fullPath' | 'name'>
): TSkippedDropzoneEntry => ({
  name: entry?.name,
  path: entry?.fullPath,
  error,
});

const mergeResults = (results: TFileReadResult[]): TFileReadResult => ({
  files: results.flatMap((result) => result.files),
  skippedEntries: results.flatMap((result) => result.skippedEntries),
});

const readEntries = (reader: TBrowserDirectoryReader): Promise<TBrowserEntry[]> =>
  new Promise((resolve, reject) => {
    reader.readEntries(resolve, reject);
  });

const readFileEntry = (entry: TBrowserFileEntry): Promise<FileWithPath> =>
  new Promise((resolve, reject) => {
    entry.file((file) => resolve(toFileWithRelativePath(file, entry.fullPath)), reject);
  });

const readEntrySafely = async (entry: TBrowserEntry): Promise<TFileReadResult> => {
  try {
    if (entry.isDirectory) return readDirectoryEntry(entry as TBrowserDirectoryEntry);
    const file = await readFileEntry(entry as TBrowserFileEntry);
    return { files: [file], skippedEntries: [] };
  } catch (error) {
    return { files: [], skippedEntries: [toSkippedEntry(error, entry)] };
  }
};

const readDirectoryEntry = async (entry: TBrowserDirectoryEntry): Promise<TFileReadResult> => {
  const reader = entry.createReader();
  const entries: TBrowserEntry[] = [];

  try {
    let keepReading = true;
    while (keepReading) {
      const batch = await readEntries(reader);
      if (batch.length === 0) keepReading = false;
      else entries.push(...batch);
    }
  } catch (error) {
    return { files: [], skippedEntries: [toSkippedEntry(error, entry)] };
  }

  const results = await Promise.all(entries.map(readEntrySafely));
  return mergeResults(results);
};

const readDataTransferItemSafely = async (item: DataTransferItem): Promise<TFileReadResult> => {
  const entry = typeof item.webkitGetAsEntry === 'function' ? (item.webkitGetAsEntry() as TBrowserEntry | null) : null;

  if (entry) return readEntrySafely(entry);

  const file = item.getAsFile();
  if (!file) {
    return {
      files: [],
      skippedEntries: [toSkippedEntry(new Error('DataTransferItem is not a readable file.'))],
    };
  }

  return { files: [toFileWithRelativePath(file)], skippedEntries: [] };
};

const getInputFiles = (event: { target: EventTarget | null }): FileWithPath[] => {
  const target = event.target as HTMLInputElement | null;
  return fromList<File>(target?.files).map((file) => toFileWithRelativePath(file));
};

const getFileHandleFiles = async (handles: TBrowserFileHandle[]): Promise<TFileReadResult> => {
  const results = await Promise.all(
    handles.map(async (handle): Promise<TFileReadResult> => {
      try {
        const file = await handle.getFile();
        return { files: [toFileWithRelativePath(file)], skippedEntries: [] };
      } catch (error) {
        return { files: [], skippedEntries: [{ name: handle.name, error }] };
      }
    })
  );

  return mergeResults(results);
};

const getDataTransferFiles = async (
  dataTransfer: DataTransfer,
  eventType: string,
  options: TDropzoneFilesOptions
): Promise<Array<FileWithPath | DataTransferItem>> => {
  if (dataTransfer.items) {
    const items = fromList<DataTransferItem>(dataTransfer.items).filter((item) => item.kind === 'file');
    if (eventType !== 'drop') return items;

    const result = mergeResults(await Promise.all(items.map(readDataTransferItemSafely)));
    if (result.skippedEntries.length > 0) options.onSkippedEntries?.(result.skippedEntries);
    return result.files;
  }

  return fromList<File>(dataTransfer.files).map((file) => toFileWithRelativePath(file));
};

export const getFilesFromDropEvent = async (
  event: DropEvent,
  options: TDropzoneFilesOptions = {}
): Promise<Array<FileWithPath | DataTransferItem>> => {
  if (Array.isArray(event)) {
    const result = await getFileHandleFiles(event);
    if (result.skippedEntries.length > 0) options.onSkippedEntries?.(result.skippedEntries);
    return result.files;
  }

  if (isObject(event)) {
    const dataTransfer = (event as DragEvent).dataTransfer;
    if (dataTransfer) return getDataTransferFiles(dataTransfer, event.type, options);
  }

  if (isObject(event) && isObject(event.target)) {
    return getInputFiles(event);
  }

  return [];
};
